import { createFileRoute, Link } from '@tanstack/react-router';
import { Target, Eye, Shield, Zap, Users, Globe, LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ContentHeader, ContentSection } from "@/components/content";

export const Route = createFileRoute('/info/about')({
  component: AboutPage,
  head: () => ({
    meta: [
      { title: 'About Us | Matricks' },
      { name: 'description', content: 'Learn about Matricks - our mission to simplify enterprise integration and AI agent management for businesses worldwide.' },
    ],
  }),
});

const iconMap: Record<string, LucideIcon> = {
  Shield,
  Zap,
  Users,
  Globe,
};

const values = [
  {
    iconName: "Shield",
    title: "Security First",
    description:
      "We believe security should never be an afterthought. Every feature we build starts with a security-first mindset, ensuring your data and systems are protected at every layer.",
  },
  {
    iconName: "Zap",
    title: "Developer Experience",
    description:
      "Great tools should be a joy to use. We obsess over the developer experience, making complex integrations simple and intuitive without sacrificing power.",
  },
  {
    iconName: "Users",
    title: "Customer Success",
    description:
      "Your success is our success. We're committed to providing exceptional support and working closely with our customers to ensure they achieve their goals.",
  },
  {
    iconName: "Globe",
    title: "Open Standards",
    description:
      "We embrace open standards and interoperability. Our platform is built to work with your existing tools and workflows, not replace them.",
  },
];

function AboutPage() {
  return (
    <div className="py-16 sm:py-24">
      <div className="container px-6">
        {/* Header */}
        <ContentHeader
          title="Building the future of enterprise integration"
          description="We're on a mission to make API integration and AI agent management accessible to every business, regardless of size or technical expertise."
          centered
        />

        {/* Mission & Vision */}
        <div className="mt-20 grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div className="rounded-2xl border bg-card p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Target className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">Our Mission</h2>
            </div>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              To democratize enterprise-grade API integration and AI agent management. We believe
              that every business should have access to powerful integration tools without needing
              a team of engineers to build and maintain them.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              By providing a unified platform that combines API gateway capabilities with AI agent
              orchestration, we enable teams to focus on building great products instead of wrestling
              with integration complexity.
            </p>
          </div>

          <div className="rounded-2xl border bg-card p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Eye className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">Our Vision</h2>
            </div>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              A world where connecting systems, services, and AI agents is as simple as a few clicks.
              Where security and compliance are built-in, not bolted-on. Where teams can innovate
              faster because they're not held back by integration challenges.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              We envision Matricks as the central nervous system for modern enterprises - connecting
              every API, database, and AI model through a single, secure, and intelligent platform.
            </p>
          </div>
        </div>

        {/* Values */}
        <ContentSection
          title="Our Values"
          description="The principles that guide everything we do."
          className="mt-24"
        >
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            {values.map((value) => (
              <div key={value.title} className="flex gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                  {(() => {
                    const Icon = iconMap[value.iconName] || Shield;
                    return <Icon className="h-6 w-6 text-primary" />;
                  })()}
                </div>
                <div>
                  <h3 className="font-semibold">{value.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </ContentSection>

        {/* CTA */}
        <div className="mt-24 rounded-2xl bg-primary/5 p-8 sm:p-12 text-center">
          <h2 className="text-2xl font-bold">Join us on our mission</h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            We're always looking for talented people who share our passion for building great products.
            Get in touch to learn more about opportunities at Matricks.
          </p>
          <div className="mt-8">
            <Button size="lg" asChild>
              <Link to="/info/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
